import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaPlus,
  FaUser,
  FaCheck,
  FaFileMedical,
  FaFilter,
  FaTable,
  FaCheck as FaCheckIcon,
  FaBaby,
  FaEye,
} from "react-icons/fa";
import { IoIosWoman, IoMdClose } from "react-icons/io";
import api from "../../../api";
import {
  usePermissions,
  useUser,
  useUserRoles,
} from "../../../authentication/authentication";
import { apiRequest, apiSuccess } from "../../../redux/api/apiActions";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import formatDate from "../../../utils/formatDate";
import { Accessibility, CalendarIcon, Check, MapPinIcon, PhoneIcon, UserIcon } from "lucide-react";
import { BsEnvelopeFill } from "react-icons/bs";
import { IoMan, IoWoman } from "react-icons/io5";
import {
  MdOutlineElderlyWoman,
  MdOutlineElderly,
  MdMan2,
  MdMan,
  MdBoy,
  MdGirl,
} from "react-icons/md";
import { CgBoy, CgGirl } from "react-icons/cg";
import FilterSide from "../../common/FilterSide";
import { HiViewGrid } from "react-icons/hi";
import Txt from "../../Text";
import { arClass } from "../../../utils/useArClass";
import textTranslator from "../../../utils/useTextTranslator";
import { useNavigate } from "react-router-dom";
import { isAdminRole } from "../../../utils/adminChecker";
import { checkAccess } from "../../../utils/usePageAccess";
import UnauthorizedUserPermissions from "../../UnauthorizedUserPermissions";

const Text = ({ children, className, page = "PATIENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const PatientSkeleton = ({ darkMode }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={arClass(
      `relative rounded-2xl border-2 p-6 overflow-hidden max-sm:p-4 max-sm:rounded-xl ${
        darkMode
          ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700"
          : "bg-gradient-to-br from-white to-gray-50 border-gray-100"
      }`
    )}
  >
    {/* Animated Shimmer Effect */}
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
      animate={{
        x: ["0%", "100%"],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut",
      }}
    />

    {/* Header Section Skeleton */}
    <div
      className={arClass(
        "flex items-start justify-between mb-4 relative z-20 max-sm:mb-3"
      )}
    >
      <div className={arClass("flex items-center gap-3 max-sm:gap-2")}>
        <motion.div
          className="relative"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div
            className={arClass(
              `w-14 h-14 rounded-full shadow-sm border max-sm:w-12 max-sm:h-12 ${
                darkMode
                  ? "bg-gray-700 border-gray-600"
                  : "bg-gray-200 border-gray-300"
              }`
            )}
          />
        </motion.div>
        <div className={arClass("space-y-2 max-sm:space-y-1")}>
          <motion.div
            className={arClass(
              `h-5 w-32 rounded max-sm:h-4 max-sm:w-24 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`
            )}
            animate={{ opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.1 }}
          />
          <motion.div
            className={arClass(
              `h-3 w-24 rounded max-sm:h-2.5 max-sm:w-20 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`
            )}
            animate={{ opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          />
          <motion.div
            className={arClass(
              `h-3 w-20 rounded max-sm:h-2.5 max-sm:w-16 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`
            )}
            animate={{ opacity: [0.6, 0.9, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
          />
        </div>
      </div>
    </div>

    {/* Patient Details Skeleton */}
    <div
      className={arClass(
        "mt-6 space-y-3 text-sm relative z-20 max-sm:mt-4 max-sm:space-y-2 max-sm:text-xs"
      )}
    >
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className={arClass("flex items-center gap-2")}
          animate={{ opacity: [0.6, 0.9, 0.6] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.1,
          }}
        >
          <div
            className={arClass(
              `w-4 h-4 rounded max-sm:w-3 max-sm:h-3 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`
            )}
          />
          <div
            className={arClass(
              `h-3 flex-1 rounded max-sm:h-2.5 ${
                darkMode ? "bg-gray-700" : "bg-gray-200"
              }`
            )}
          />
        </motion.div>
      ))}
    </div>

    {/* Enhanced Shadow */}
    <div className="absolute inset-0 rounded-2xl shadow-lg opacity-20" />
  </motion.div>
);

const EmptyPatientState = ({
  darkMode,
  hasActiveFilters,
  clearAllFilters,
  setSearchTerm,
  debouncedSearch,
  onOpenNewPatientModal,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={arClass(
        "flex flex-col items-center justify-center py-16 px-6 text-center w-full max-sm:py-8 max-sm:px-4"
      )}
    >
      {/* Animated Icon Container */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: 0.2,
          type: "spring",
          stiffness: 200,
        }}
        className="relative mb-8 max-sm:mb-6"
      >
        {/* Background Gradient */}
        <div
          className={arClass(
            `absolute inset-0 rounded-2xl transform rotate-6 scale-110 max-sm:rounded-xl ${
              darkMode
                ? "bg-gradient-to-br from-blue-900/20 to-indigo-900/20"
                : "bg-gradient-to-br from-blue-50 to-indigo-100"
            }`
          )}
        ></div>

        {/* Main Icon */}
        <div
          className={arClass(
            `relative rounded-xl p-6 shadow-lg border max-sm:p-4 ${
              darkMode
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-100"
            }`
          )}
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-2 max-sm:w-12 max-sm:h-12">
            <FaUser className="w-8 h-8 text-white max-sm:w-6 max-sm:h-6" />
          </div>
        </div>

        {/* Floating Medical Icons */}
        <motion.div
          animate={{
            y: [0, -12, 0],
            rotate: [0, 8, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-2 -right-2 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center shadow-lg max-sm:w-6 max-sm:h-6"
        >
          <FaPlus className="w-4 h-4 text-white max-sm:w-3 max-sm:h-3" />
        </motion.div>

        <motion.div
          animate={{
            y: [0, -8, 0],
            rotate: [0, -5, 0],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className="absolute -bottom-2 -left-2 w-6 h-6 bg-purple-400 rounded-full flex items-center justify-center shadow-lg max-sm:w-5 max-sm:h-5"
        >
          <FaFileMedical className="w-3 h-3 text-white max-sm:w-2.5 max-sm:h-2.5" />
        </motion.div>

        {/* Additional Floating Icon */}
        <motion.div
          animate={{
            y: [0, -10, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
          className="absolute -top-4 left-4 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg max-sm:w-4 max-sm:h-4"
        >
          <FaSearch className="w-2.5 h-2.5 text-white max-sm:w-2 max-sm:h-2" />
        </motion.div>
      </motion.div>

      {/* Text Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="max-w-md"
      >
        <h3
          className={arClass(
            `text-sm sm:text-lg font-bold mb-3 ${
              darkMode ? "text-gray-100" : "text-gray-800"
            }`
          )}
        >
          {debouncedSearch || hasActiveFilters
            ? textTranslator("noPatientsFound", "PATIENTS")
            : textTranslator("noPatientsYet", "PATIENTS")}
        </h3>

        <p
          className={arClass(
            `mb-6 leading-relaxed text-xs sm:text-sm ${
              darkMode ? "text-gray-300" : "text-gray-600"
            }`
          )}
        >
          {debouncedSearch
            ? textTranslator("noPatientsSearch", "PATIENTS")
            : hasActiveFilters
            ? textTranslator("noPatientsFilters", "PATIENTS")
            : textTranslator("noPatientsEmpty", "PATIENTS")}
        </p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={arClass(
            "flex flex-col sm:flex-row gap-3 justify-center max-sm:gap-2"
          )}
        >
          {(debouncedSearch || hasActiveFilters) && (
            <motion.button
              onClick={() => {
                setSearchTerm?.("");
                clearAllFilters?.();
              }}
              className={arClass(
                `px-6 py-3 rounded-lg font-semibold border transition-all duration-200 shadow-sm hover:shadow-lg text-xs sm:text-base ${
                  darkMode
                    ? "bg-red-800 border-red-700 hover:bg-red-700 hover:border-red-600 text-red-300"
                    : "bg-red-100 border-red-400 hover:bg-red-800 hover:border-red-300 text-red-800 hover:text-red-200"
                }`
              )}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {debouncedSearch && hasActiveFilters
                ? textTranslator("clearAll", "PATIENTS")
                : debouncedSearch
                ? textTranslator("clearSearch", "PATIENTS")
                : textTranslator("clearFilters", "PATIENTS")}
            </motion.button>
          )}

          <motion.button
            onClick={() => onOpenNewPatientModal && onOpenNewPatientModal(true)}
            className={arClass(
              `px-6 py-3 rounded-lg font-semibold border transition-all duration-200 shadow-sm hover:shadow-lg text-xs sm:text-base ${
                darkMode
                  ? "bg-blue-800 border-blue-700 hover:bg-blue-700 hover:border-blue-600 text-blue-300"
                  : "bg-blue-100 border-blue-400 hover:bg-blue-800 hover:border-blue-300 text-blue-800 hover:text-blue-200"
              }`
            )}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {debouncedSearch || hasActiveFilters
              ? textTranslator("addNewPatient", "PATIENTS")
              : textTranslator("addFirstPatient", "PATIENTS")}
          </motion.button>
        </motion.div>

        {/* Help Text */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className={arClass(
            `mt-6 p-4 rounded-lg border max-sm:mt-4 max-sm:p-3 ${
              darkMode
                ? "bg-blue-900/20 border-blue-800"
                : "bg-blue-50 border-blue-100"
            }`
          )}
        >
          <div className={arClass("flex items-start gap-3 max-sm:gap-2")}>
            <FaFileMedical className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0 max-sm:w-4 max-sm:h-4" />
            <div className="text-left">
              <p
                className={arClass(
                  `text-sm font-medium max-sm:text-xs ${
                    darkMode ? "text-blue-300" : "text-blue-800"
                  }`
                )}
              >
                <Text>gettingStarted</Text>
              </p>
              <p
                className={arClass(
                  `text-xs max-sm:text-xsm ${
                    darkMode ? "text-blue-400" : "text-blue-600"
                  }`
                )}
              >
                <Text>gettingStartedDescription</Text>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

const TableRow = ({
  patient,
  selectedPatient,
  darkMode,
  handleSelect,
  avatarSelector,
  calculateAge,
}) => {
  const isSelected = selectedPatient?.id === patient.id;
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const border = defaultLanguage === "ar" ? "border-r-4" : "border-l-4";

  return (
    <tr
      className={`cursor-pointer transition-colors duration-200 ${
        isSelected
          ? darkMode
            ? `bg-gradient-to-r from-blue-900/20 to-blue-900/10 border-blue-500 ${border}`
            : `bg-gradient-to-r from-blue-50 to-blue-100/50 border-blue-500 ${border}`
          : darkMode
          ? `hover:bg-gray-800/50 ${border} border-transparent`
          : `hover:bg-gray-50/80 ${border} border-transparent`
      }`}
      onClick={() => handleSelect(patient)}
    >
      <td className={arClass("px-2 py-2 w-8 border-r max-sm:text-xs")}>
        {patient.id}
      </td>
      <td className={arClass("py-4 px-4")}>
        <div className={arClass("flex items-center gap-3")}>
          <img
            src={avatarSelector(patient)}
            alt={`${patient.firstName} ${patient.lastName}`}
            className="w-8 h-8 rounded-full"
          />
          <div>
            <div
              className={arClass(
                `font-medium text-sm ${
                  darkMode ? "text-white" : "text-gray-900"
                }`
              )}
            >
              {patient.firstName} {patient.lastName}
            </div>
            <div
              className={arClass(
                `text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`
              )}
            >
              {patient.cin || "-"}
            </div>
          </div>
        </div>
      </td>
      <td className={arClass("py-4 px-4")}>
        {calculateAge ? calculateAge(patient.birthday) : patient.age}
      </td>
      <td className={arClass("py-4 px-4")}>{patient.phone || "-"}</td>
    </tr>
  );
};



const PatientSelection = ({
  darkMode,
  colors,
  onPatientSelect,
  onClearSelection = null,
  selectedPatient,
  onOpenNewPatientModal = null,
  pageSize = 9,
  calculateAge,
  showAddNewPatientButton = false,
}) => {
  const canPost =checkAccess("postPatient");
  const canView = checkAccess("getPatient");
  const defaultLanguage = useSelector(
    (state) => state.settings.defaultLanguage
  );
  const patientsKey = `patients-selection`;
  const createPatientKey = "createPatient";
  const deletePatientKey = "deletePatient";
  const deleteAllPatientsKey = "deleteAllPatients";
  const { requests } = useSelector((state) => state.api);
  const deletePatient = requests[deletePatientKey]?.success;
  const deleteAllPatients = requests[deleteAllPatientsKey]?.success;
  const createPatient = requests[createPatientKey]?.success;
  const roles = useUserRoles();
  const navigate = useNavigate();
  const patientsLoading = requests[patientsKey]?.loading;
  const message = requests[patientsKey]?.message;
  const isSearchingRef = useRef(false);
  const dispatch = useDispatch();
  const [patients, setPatients] = useState([]);
  const page = 1;
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const user = useUser();
  const [viewMode, setViewMode] = useState("grid"); // 'table' or 'grid'
  const [mobileView, setMobileView] = useState("patients"); // 'patients' or 'selected'
  const { permissions, loading: permissionsLoading } = usePermissions();
   const [patientPageWidth, setPatientPageWidth] = useState(0);
  const patientRef =useRef(null);
  const isCompactLayout = patientPageWidth > 0 && patientPageWidth < 900;
  const defaultFilters = {
    minAge: "",
    maxAge: "",
    ageExact: "",
    gender: "All",
    // specific sort fields start unset so they don't mask each other
    sortDate: "createdAt-desc",
    sortName: "",
    sortBirthday: "",
  };

  const [filters, setFilters] = useState({ ...defaultFilters });

  // Fixed hasActiveFilters logic
  const hasActiveFilters =
    filters.minAge !== defaultFilters.minAge ||
    filters.maxAge !== defaultFilters.maxAge ||
    filters.ageExact !== defaultFilters.ageExact ||
    filters.gender !== defaultFilters.gender ||
    filters.sortDate !== defaultFilters.sortDate ||
    filters.sortName !== defaultFilters.sortName ||
    filters.sortBirthday !== defaultFilters.sortBirthday;

    useEffect(() => {
      const updateWidth = () => {
        if (patientRef.current) {
          setPatientPageWidth(patientRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }, []);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchPatients = useCallback(async () => {
    isSearchingRef.current = true;

    const search = debouncedSearch
      ? `&q=${encodeURIComponent(debouncedSearch)}`
      : "";
    // Only include the specific sort params if they are set (we clear others on choose)
    const nameSort = filters.sortName
      ? `&sortName=${encodeURIComponent(filters.sortName)}`
      : "";
    const dateSort = filters.sortDate
      ? `&sortDate=${encodeURIComponent(filters.sortDate)}`
      : "";
    const birthdaySort = filters.sortBirthday
      ? `&sortBirthday=${encodeURIComponent(filters.sortBirthday)}`
      : "";

    // Backend filter params
    const genderParam =
      filters.gender && filters.gender !== "All"
        ? `&gender=${encodeURIComponent(filters.gender)}`
        : "";
    const minAgeParam =
      filters.minAge && filters.minAge !== defaultFilters.minAge
        ? `&minAge=${filters.minAge}`
        : "";
    const maxAgeParam =
      filters.maxAge && filters.maxAge !== defaultFilters.maxAge
        ? `&maxAge=${filters.maxAge}`
        : "";
    const ageExactParam = filters.ageExact
      ? `&age=${encodeURIComponent(filters.ageExact)}`
      : "";

    if (
      !isAdminRole(roles) &&
      permissions &&
      !permissions.patientsPermissions?.get
    ) {
      navigate("/dashboard");
      return;
    }

    dispatch(apiRequest("GET", patientsKey));
    api
      .get(
        `patients/user-id/${user.id}?page=${page}&pageSize=${pageSize}${search}${genderParam}${minAgeParam}${maxAgeParam}${ageExactParam}${nameSort}${dateSort}${birthdaySort}`
      )
      .then((res) => {
        setPatients(res.data.results);

        isSearchingRef.current = false;
        dispatch(
          apiSuccess("GET", patientsKey, "Fetched patients successfully")
        );
      })
      .catch((error) => {
        isSearchingRef.current = false;
        const errorMessage =
          error?.response?.data?.error ||
          "Erreur lors de la récupération des données.";
        const type = error?.response?.data?.type || null;
        errorChecker(errorMessage, type, dispatch, errorMessage);
      });
  }, [
    page,
    debouncedSearch,
    user.id,
    roles,
    filters,
    dispatch,
    navigate,
    patientsKey,
    pageSize,
  ]);

  useEffect(() => {
    if (deleteAllPatients || deletePatient || createPatient) {
      fetchPatients();
      return;
    }
    fetchPatients();
  }, [
    deleteAllPatients,
    deletePatient,
    createPatient,
    fetchPatients,
    filters,
    debouncedSearch,
  ]);

  const handleSelect = (p) => {
    if (selectedPatient?.id === p.id) {
      clearSelected();
      return;
    }
    if (onPatientSelect) onPatientSelect(p);
    setMobileView("selected"); // Switch to selected view on mobile when patient is selected
  };

  const clearSelected = () => {
    if (onClearSelection) onClearSelection();
    if (onPatientSelect) onPatientSelect(null);
    setMobileView("patients"); // Switch back to patients list on mobile
  };

  const clearFilters = () => {
    setFilters({ ...defaultFilters });
  };

  // Avatar selector from Patients component
  const avatarSelector = (patient) => {
    const { gender, age } = patient;
    const calculatedAge = calculateAge ? calculateAge(patient.birthday) : age;

    if (gender === "Male") {
      if (calculatedAge < 8) return "/images/avatars/young-boy.png";
      else if (calculatedAge < 18) return "/images/avatars/boy.png";
      else if (calculatedAge < 30) return "/images/avatars/adult-man.png";
      else if (calculatedAge < 50) return "/images/avatars/man.png";
      else return "/images/avatars/old-man.png";
    } else {
      if (calculatedAge < 8) return "/images/avatars/young-girl.png";
      else if (calculatedAge < 18) return "/images/avatars/girl.png";
      else if (calculatedAge < 30) return "/images/avatars/adult-woman.png";
      else if (calculatedAge < 50) return "/images/avatars/woman.png";
      else return "/images/avatars/old-woman.png";
    }
  };

  const iconSelector = (patient) => {
    const { gender, age } = patient;
    const calculatedAge = calculateAge ? calculateAge(patient.birthday) : age;

    if (gender === "Male") {
      if (calculatedAge < 8) return <FaBaby className="text-white" size={20} />;
      else if (calculatedAge < 18)
        return <MdBoy className="text-white" size={20} />;
      else if (calculatedAge < 30)
        return <MdMan className="text-white" size={20} />;
      else if (calculatedAge < 50)
        return <IoMan className="text-white" size={20} />;
      else return <MdOutlineElderly className="text-white" size={20} />;
    } else {
      if (calculatedAge < 8) return <FaBaby className="text-white" size={20} />;
      else if (calculatedAge < 18)
        return <MdGirl className="text-white" size={20} />;
      else if (calculatedAge < 30)
        return <IoWoman className="text-white" size={20} />;
      else if (calculatedAge < 50)
        return <IoIosWoman className="text-white" size={20} />;
      else return <MdOutlineElderlyWoman className="text-white" size={20} />;
    }
  };

   if(!canView) {
    
    return (
      <UnauthorizedUserPermissions
                  permissionsKey="patientsPermissions"
                  method="get"
                />
    );
  }

  return (
    <div className={arClass("space-y-6 max-sm:space-y-0")} ref={patientRef} >
      {/* Mobile Navigation Tabs */}
      <div className={arClass(`${isCompactLayout ? "block" : "hidden"} mb-4 rounded-lg`)}>
        <div className={arClass("flex border rounded-lg overflow-hidden")}>
          <button
            onClick={() => setMobileView("patients")}
            className={`flex-1 py-3 text-center font-medium transition-colors text-xs  ${
              mobileView === "patients"
                ? darkMode
                  ? "bg-blue-600 text-white"
                  : "bg-blue-500 text-white"
                : darkMode
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }
            ${defaultLanguage === "ar" ? "rounded-r-lg" : "rounded-l-lg"}
            `}
          >
            <Text>Patients List</Text>
          </button>
          <button
            onClick={() => selectedPatient && setMobileView("selected")}
            disabled={!selectedPatient}
            className={arClass(`flex-1 py-3 text-center font-medium transition-colors text-xs ${
              mobileView === "selected"
                ? darkMode
                  ? "bg-blue-600 text-white"
                  : "bg-blue-500 text-white"
                : !selectedPatient
                ? darkMode
                  ? "bg-gray-800 text-gray-500 cursor-not-allowed"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
                : darkMode
                ? "bg-gray-800 text-gray-300 hover:bg-gray-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }
            ${defaultLanguage === "ar" ? "rounded-l-lg" : "rounded-r-lg"}
            `)}
          >
            <Text>Selected Patient</Text>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div
        className={arClass(
          "flex items-center gap-4 max-sm:flex-col max-sm:items-start"
        )}
      >
        <FilterSide
          searchTerm={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFilterChange={(key, value) => {
            // When a sort is chosen, set the main sort and clear the other specific sorts
            if (key.startsWith("sort")) {
              setFilters((prev) => ({
                ...prev,
                sortDate: key === "sortDate" ? value : null,
                sortName: key === "sortName" ? value : null,
                sortBirthday: key === "sortBirthday" ? value : null,
                [key]: value,
              }));
            } else {
              setFilters((prev) => ({ ...prev, [key]: value }));
            }
          }}
          sortOptions={{
            sortDate: filters.sortDate,
            sortName: filters.sortName,
            sortBirthday: filters.sortBirthday,
          }}
          onClearFilters={clearFilters}
          darkMode={darkMode}
          searchPlaceholder={textTranslator(
            "Search patients by name, CIN, phone, or email...",
            "PATIENTS"
          )}
          filterTitle={textTranslator("Filter Patients", "PATIENTS")}
          filterOptions={{
            sections: [
              {
                key: "sortDate",
                label: textTranslator("Trier (Date)", "PATIENTS"),
                type: "buttons",
                options: [
                  {
                    label: textTranslator("Newest First", "PATIENTS"),
                    value: "createdAt-desc",
                  },
                  {
                    label: textTranslator("Oldest First", "PATIENTS"),
                    value: "createdAt-asc",
                  },
                ],
              },
              {
                key: "sortName",
                label: textTranslator("Trier (Name)", "PATIENTS"),
                type: "buttons",
                options: [
                  {
                    label: textTranslator("First name (A → Z)", "PATIENTS"),
                    value: "firstName-asc",
                  },
                  {
                    label: textTranslator("First name (Z → A)", "PATIENTS"),
                    value: "firstName-desc",
                  },
                  {
                    label: textTranslator("Last name (A → Z)", "PATIENTS"),
                    value: "lastName-asc",
                  },
                  {
                    label: textTranslator("Last name (Z → A)", "PATIENTS"),
                    value: "lastName-desc",
                  },
                ],
              },
              {
                key: "sortBirthday",
                label: textTranslator("Trier (Birthday)", "PATIENTS"),
                type: "buttons",
                options: [
                  {
                    label: textTranslator("Youngest First", "PATIENTS"),
                    value: "birthday-desc",
                  },
                  {
                    label: textTranslator("Oldest First", "PATIENTS"),
                    value: "birthday-asc",
                  },
                ],
              },
              {
                key: "gender",
                label: textTranslator("Genre", "PATIENTS"),
                type: "buttons",
                options: [
                  {
                    label: textTranslator("All genders", "PATIENTS"),
                    value: "All",
                  },
                  {
                    label: textTranslator("Male", "PATIENTS"),
                    value: "Male",
                  },
                  {
                    label: textTranslator("Female", "PATIENTS"),
                    value: "Female",
                  },
                ],
              },
              {
                key: "minAge",
                label: textTranslator("Age Min", "PATIENTS"),
                type: "input",
                options: {
                  placeholder: textTranslator("Min age", "PATIENTS"),
                },
              },
              {
                key: "maxAge",
                label: textTranslator("Age Max", "PATIENTS"),
                type: "input",
                options: {
                  placeholder: textTranslator("Max age", "PATIENTS"),
                },
              },
              {
                key: "ageExact",
                label: textTranslator("Exact Age", "PATIENTS"),
                type: "input",
                options: {
                  placeholder: textTranslator("e.g. 35", "PATIENTS"),
                },
              },
              {
                key: "columns",
                label: textTranslator("Visible Columns", "PATIENTS"),
                type: "columns",
              },
            ],
          }}
          filterLabels={{
            filterBy: textTranslator("Filter By", "PATIENTS"),
            columns: textTranslator("Visible Columns", "PATIENTS"),
          }}
          visibleColumns={null}
          onToggleColumn={() => {}}
          viewMode="grid"
        />
        {/* View Mode Toggle - Desktop Only */}
        <div
          className={arClass(
            "flex justify-end my-4 max-sm:mt-0 max-sm:mb-4 max-sm:w-full"
          )}
        >
          {showAddNewPatientButton && (
            <button
              onClick={() =>
                onOpenNewPatientModal && onOpenNewPatientModal(true)
              }
              className={arClass(`flex items-center gap-2 px-4 py-2 sm:py-3 mr-4 rounded-lg font-medium border min-w-max
                transition-colors duration-200 shadow-sm hover:shadow-lg max-sm:flex-1 max-sm:justify-center max-sm:text-xs
                ${
                  darkMode
                    ? "!bg-green-700 text-green-200 border-green-600 border hover:!bg-green-600"
                    : "!bg-green-300 text-green-800 border-green-400 border hover:!bg-green-100"
                }`)}
            >
              <Text>addNewPatient</Text>
            </button>
          )}
          <div className={arClass("flex rounded-lg border")}>
                          <button
                            onClick={() => {
                              setViewMode("table");
                             
                            }}
                            className={`p-3 transition-colors max-sm:p-2 ${
                              viewMode === "table"
                                ? "bg-blue-600 !text-white"
                                : darkMode
                                ? "!bg-gray-700 !text-gray-300 hover:!bg-gray-600"
                                : "!bg-gray-100 !text-gray-600 hover:!bg-gray-200"
                            } 
                                      ${
                                        defaultLanguage === "ar"
                                          ? "rounded-r-lg"
                                          : "rounded-l-lg"
                                      }
                                      `}
                          >
                            <FaTable size={20} />
                          </button>
                          <button
                            onClick={() => setViewMode("grid")}
                            className={`p-3 transition-colors max-sm:p-2 ${
                              viewMode === "grid"
                                ? "bg-blue-600 !text-white"
                                : darkMode
                                ? "!bg-gray-700 !text-gray-300 hover:!bg-gray-600"
                                : "!bg-gray-100 !text-gray-600 hover:!bg-gray-200"
                            }
                                       
                                      ${
                                        defaultLanguage === "ar"
                                          ? "rounded-l-lg"
                                          : "rounded-r-lg"
                                      }
                                      `}
                          >
                            <HiViewGrid size={20} />
                          </button>
                        </div>
          
        </div>
      </div>

      
      {/* Main Content Area */}
      <div
        className={arClass(
         `grid  gap-6 max-sm:grid-cols-1 max-sm:gap-0 ${isCompactLayout ? "grid-cols-1" : "grid-cols-12"}`
        )}
      >
        {/* Left Side - Patient Grid/Table */}
        <div
          className={arClass(
            `col-span-8 space-y-6 h-full flex flex-col max-sm:col-span-1 ${
              isCompactLayout && mobileView !== "patients" ? "hidden" : ""
            }`
          )}
        >
          {patientsLoading ? (
            <div className="flex-1 h-full">
              <motion.div
                className={arClass(
                  "grid gap-6 2xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 max-sm:gap-4"
                )}
                initial="hidden"
                animate="visible"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.1,
                    },
                  },
                }}
              >
                {[...Array(6)].map((_, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <PatientSkeleton darkMode={darkMode} />
                  </motion.div>
                ))}
              </motion.div>
            </div>
          ) : patients.length > 0 && patients !== "No data available" ? (
            <div
              className={arClass(
                `flex-1 h-full  rounded-lg border ${
                  viewMode === "table" ? "border" : ""
                } transition-colors duration-300 ${colors.card} overflow-auto`
              )}
            >
              <div className={arClass(`flex items-center gap-2 px-3 py-2 mx-2 mt-2 mb-6 rounded cursor-pointer text-sm max-sm:text-xs ${
              darkMode
                  ? "!bg-orange-900 !border-orange-300 hover:!bg-orange-600 text-orange-300"
                  : "!bg-orange-100 !text-orange-600 hover:!bg-orange-200 "
            }`)}> 
                <Text>Please select a patient. If you don’t see the patient in the list, use the search or filter options to locate them.</Text> 
        
      </div>
              {/* Grid View */}
              {viewMode === "grid" ? (
                <motion.div
                  className={arClass(
                    "grid gap-6 p-2 2xl:grid-cols-2 lg:grid-cols-2 md:grid-cols-1 sm:grid-cols-1 max-sm:gap-4"
                  )}
                  initial="hidden"
                  animate="visible"
                  variants={{
                    visible: {
                      transition: {
                        staggerChildren: 0.1,
                      },
                    },
                  }}
                >
                  <AnimatePresence>
                    {patients.map((p, index) => (
                      <motion.div
                        key={p.id}
                        layout
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={{
                          hidden: {
                            opacity: 0,
                            y: 20,
                            scale: 0.9,
                          },
                          visible: {
                            opacity: 1,
                            y: 0,
                            scale: 1,
                            transition: {
                              type: "spring",
                              stiffness: 300,
                              damping: 24,
                              delay: index * 0.05,
                            },
                          },
                          exit: {
                            opacity: 0,
                            scale: 0.8,
                            transition: { duration: 0.2 },
                          },
                        }}
                        whileHover="hover"
                        className="relative"
                      >
                        <motion.div
                          variants={{
                            hover: {
                              y: -4,
                              transition: {
                                type: "spring",
                                stiffness: 400,
                                damping: 25,
                              },
                            },
                          }}
                          className={arClass(`
                            relative rounded-xl border p-4 shadow-sm overflow-hidden cursor-pointer max-sm:p-3
                            ${
                              selectedPatient?.id === p.id
                                ? darkMode
                                  ? "border-blue-500 bg-blue-500/10"
                                  : "border-blue-500 bg-blue-50"
                                : darkMode
                                ? "bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700"
                                : "bg-gradient-to-br from-white to-gray-50 border-gray-100"
                            }
                          `)}
                          onClick={() => handleSelect(p)}
                        >
                          {/* Header Section */}
                          <div
                            className={arClass(
                              "flex items-start justify-between mb-3 relative z-20"
                            )}
                          >
                            <div className={arClass("flex items-center gap-3")}>
                              <motion.div
                                className="relative"
                                whileHover={{ scale: 1.05 }}
                                transition={{
                                  type: "spring",
                                  stiffness: 400,
                                  damping: 10,
                                }}
                              >
                                <motion.img
                                  src={avatarSelector(p)}
                                  alt={`${p.firstName} ${p.lastName}`}
                                  className={arClass(
                                    `w-12 h-12 rounded-full shadow-sm border max-sm:w-10 max-sm:h-10 ${
                                      darkMode
                                        ? "bg-gray-900 border-gray-100"
                                        : "bg-gray-100 border-gray-300"
                                    }`
                                  )}
                                  whileHover={{ rotate: 5 }}
                                  transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 10,
                                  }}
                                />
                                {selectedPatient?.id === p.id && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 border-2 border-white max-sm:-top-0.5 max-sm:-right-0.5"
                                  >
                                    <FaCheck className="text-white text-xs max-sm:text-xsm" />
                                  </motion.div>
                                )}
                              </motion.div>
                              <div className="max-w-[calc(100%-60px)]">
                                <motion.h3
                                  className={arClass(
                                    `font-bold text-base tracking-tight truncate max-sm:text-sm ${
                                      darkMode ? "text-white" : "text-gray-900"
                                    }`
                                  )}
                                  whileHover={{ x: 2 }}
                                  title={`${p.firstName} ${p.lastName}`}
                                >
                                  {p.firstName} {p.lastName}
                                </motion.h3>
                                <motion.p
                                  className={arClass(
                                    `text-xs truncate max-sm:text-xsm ${
                                      darkMode
                                        ? "text-gray-300"
                                        : "text-gray-600"
                                    }`
                                  )}
                                >
                                  {textTranslator("CIN", "PATIENTS")}:{" "}
                                  {p.cin || "-"}
                                </motion.p>
                                <motion.p
                                  className={arClass(
                                    `block text-xs max-sm:text-xsm ${
                                      darkMode
                                        ? "text-gray-300"
                                        : "text-gray-600"
                                    }`
                                  )}
                                >
                                  {textTranslator("Age", "PATIENTS")}:{" "}
                                  {calculateAge
                                    ? calculateAge(p.birthday)
                                    : p.age}
                                </motion.p>
                              </div>
                            </div>
                          </div>

                          {/* Compact Patient Details */}
                          <motion.div
                            className={arClass(
                              "space-y-2 text-xs max-sm:space-y-1.5 max-sm:text-xsm"
                            )}
                            variants={{
                              hover: {
                                transition: {
                                  staggerChildren: 0.1,
                                },
                              },
                            }}
                          >
                            <motion.div
                              className={arClass("flex items-center gap-2")}
                              variants={{
                                hover: { x: 2 },
                              }}
                            >
                              <PhoneIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span
                                className={arClass(
                                  `truncate ${
                                    darkMode ? "text-gray-300" : "text-gray-600"
                                  }`
                                )}
                                title={p.phone || "-"}
                              >
                                {p.phone || "-"}
                              </span>
                            </motion.div>

                            <motion.div
                              className={arClass("flex items-center gap-2")}
                              variants={{
                                hover: { x: 2 },
                              }}
                            >
                              <BsEnvelopeFill className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span
                                className={arClass(
                                  `truncate ${
                                    darkMode ? "text-gray-300" : "text-gray-600"
                                  }`
                                )}
                                title={p.email || "-"}
                              >
                                {p.email || "-"}
                              </span>
                            </motion.div>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              ) : (
                // Table View for Mobile
                <div className="overflow-x-auto">
                  <table
                    className={arClass("min-w-[500px] w-full border-collapse")}
                  >
                    <thead className={arClass("px-1")}>
                      <tr
                        className={arClass(
                          `border-b ${
                            darkMode
                              ? "border-gray-700 bg-gray-800"
                              : "border-gray-200 bg-gray-50"
                          }`
                        )}
                      >
                        <th
                          className={arClass(
                            `py-3 px-4 text-xs font-medium w-8 ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`
                          )}
                        >
                          <Text>ID</Text>
                        </th>
                        <th
                          className={arClass(
                            `py-3 px-4 text-xs font-medium ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`
                          )}
                        >
                          <Text>Patient</Text>
                        </th>
                        <th
                          className={arClass(
                            `py-3 px-4 text-xs font-medium ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`
                          )}
                        >
                          <Text>Age</Text>
                        </th>
                        <th
                          className={arClass(
                            `py-3 px-4 text-xs font-medium ${
                              darkMode ? "text-gray-300" : "text-gray-600"
                            }`
                          )}
                        >
                          <Text>Phone</Text>
                        </th>
                      </tr>
                    </thead>
                    <tbody className={arClass("px-1")}>
                      {patients.map((p) => (
                        <TableRow
                          key={p.id}
                          patient={p}
                          selectedPatient={selectedPatient}
                          darkMode={darkMode}
                          handleSelect={handleSelect}
                          avatarSelector={avatarSelector}
                          calculateAge={calculateAge}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <EmptyPatientState
              darkMode={darkMode}
              hasActiveFilters={hasActiveFilters}
              clearAllFilters={clearFilters}
              setSearchTerm={setSearchQuery}
              debouncedSearch={debouncedSearch}
              onOpenNewPatientModal={onOpenNewPatientModal}
            />
          )}
        </div>

        {/* Right Side - Selected Patient Card */}
        <div
          className={arClass(
            `col-span-4 h-full ${
              isCompactLayout && mobileView !== "selected" ? "hidden" : ""
            } ${patientsLoading ? "max-h-[calc(100%)]" : "h-full"}`
          )}
        >
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className={arClass(
              `h-full flex flex-col rounded-2xl border-2 bg-gradient-to-br max-sm:rounded-xl max-sm:border ${isCompactLayout ? 'w-full' : 'w-auto'} ${
                darkMode
                  ? "from-blue-900/20 to-cyan-900/20 border-blue-400/30"
                  : "from-blue-50 to-cyan-50 border-blue-200"
              } shadow-xl overflow-hidden`
            )}
          >
            {selectedPatient ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className={arClass(`flex-1 overflow-auto ${isCompactLayout ? 'w-full' : 'w-auto'}`)}
              >
                {/* Header Section */}
                <div
                  className={arClass(
                    "p-6 border-b border-blue-200/30 bg-gradient-to-r from-blue-500/5 to-cyan-500/5 max-sm:p-4"
                  )}
                >
                  <div
                    className={arClass(
                      "flex items-start justify-between mb-4 max-sm:mb-3"
                    )}
                  >
                    <div
                      className={arClass(
                        "flex items-start gap-4 flex-1 max-sm:gap-3"
                      )}
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 400,
                          damping: 10,
                          delay: 0.3,
                        }}
                        className="relative flex-shrink-0"
                      >
                        <motion.img
                          src={avatarSelector(selectedPatient)}
                          alt={`${selectedPatient.firstName} ${selectedPatient.lastName}`}
                          className={arClass(
                            `w-20 h-20 rounded-full shadow-lg border-2 max-sm:w-16 max-sm:h-16 ${
                              darkMode ? "border-blue-400" : "border-blue-200"
                            }`
                          )}
                          whileHover={{ scale: 1.05, rotate: 2 }}
                          transition={{
                            type: "spring",
                            stiffness: 300,
                            damping: 10,
                          }}
                        />
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5 }}
                          className={arClass(
                            `absolute -bottom-2 -right-2 w-7 h-7 rounded-full border-2 max-sm:w-6 max-sm:h-6 ${
                              darkMode ? "border-gray-800" : "border-white"
                            } flex items-center justify-center ${
                              selectedPatient.gender === "Male"
                                ? "bg-blue-500"
                                : "bg-pink-500"
                            }`
                          )}
                        >
                          {React.cloneElement(iconSelector(selectedPatient), {
                            size: 14,
                          })}
                        </motion.div>
                      </motion.div>

                      <motion.div
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="flex-1 min-w-0"
                      >
                        <div
                          className={arClass(
                            "flex items-center justify-between gap-3 mb-2 max-sm:gap-2"
                          )}
                        >
                          <h3
                            className={arClass(
                              `text-2xl font-bold ${colors.text.primary} truncate max-sm:text-sm`
                            )}
                          >
                            {selectedPatient.firstName}{" "}
                            {selectedPatient.lastName}
                          </h3>
                          <motion.button
                            onClick={clearSelected}
                            type="button"
                            aria-label="Deselect patient"
                            className={arClass(
                              `p-1 rounded-lg flex-shrink-0 max-sm:p-0.5 ${
                                darkMode
                                  ? "bg-red-500/20 hover:bg-red-500/30"
                                  : "bg-red-50 hover:bg-red-100"
                              } transition-colors`
                            )}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 10,
                            }}
                          >
                            <IoMdClose
                              className={
                                darkMode ? "text-red-400" : "text-red-500"
                              }
                              size={16}
                            />
                          </motion.button>
                        </div>

                   

                        <div
                          className={arClass(
                            "flex flex-wrap items-center gap-1 mb-4 max-sm:mb-3 max-sm:gap-0.5"
                          )}
                        >
                          <span
                            className={arClass(
                              `text-xss font-semibold px-1 py-1 rounded-full max-sm:text-xs max-sm:px-1 ${
                                darkMode
                                  ? "bg-blue-500/20 text-blue-300 border border-blue-400/30"
                                  : "bg-blue-100 text-blue-600 border border-blue-200"
                              }`
                            )}
                          >
                            {formatDate(selectedPatient.createdAt, false)}
                          </span>
                          <span
                            className={arClass(
                              `text-xss font-semibold px-1 py-1 rounded-full max-sm:text-xs max-sm:px-1 ${
                                darkMode
                                  ? "bg-blue-500/20 text-blue-300 border border-blue-400/30"
                                  : "bg-blue-100 text-blue-600 border border-blue-200"
                              }`
                            )}
                          >
                            <Text>{selectedPatient.creator.role}</Text>.
                            {selectedPatient.creator.firstName}
                            {selectedPatient.creator.lastName}
                            <small>
                              {parseInt(user.id) ===
                              parseInt(selectedPatient.creator.id)
                                ? textTranslator(" (Me)", "PATIENTS")
                                : ""}
                            </small>
                          </span>
                        </div>
                        <div
                          className={arClass(
                            "flex flex-wrap items-center gap-3 mb-4 max-sm:gap-2 max-sm:mb-3"
                          )}
                        >
                          <span
                            className={arClass(
                              `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                                darkMode
                                  ? "bg-gray-700 text-gray-300"
                                  : "bg-gray-200 text-gray-600"
                              }`
                            )}
                          >
                            <Text>ID</Text>: {selectedPatient.id}
                          </span>
                          <span
                            className={arClass(
                              `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                                darkMode
                                  ? "bg-green-500/20 text-green-300"
                                  : "bg-green-100 text-green-600"
                              }`
                            )}
                          >
                            <Text>{selectedPatient.gender}</Text>
                          </span>
                          <span
                            className={arClass(
                              `text-xs px-2 py-1 rounded-full max-sm:text-xsm max-sm:px-1.5 max-sm:py-0.5 ${
                                darkMode
                                  ? "bg-purple-500/20 text-purple-300"
                                  : "bg-purple-100 text-purple-600"
                              }`
                            )}
                          >
                            {textTranslator("Age", "PATIENTS")}:{" "}
                            {calculateAge
                              ? calculateAge(selectedPatient.birthday)
                              : selectedPatient.age}
                          </span>
                        </div>
                      </motion.div>
                    </div>
                  </div>

                  {/* Quick Stats Grid */}
                  <motion.div
                    className={arClass(
                      "grid grid-cols-2 gap-4 px-6 mb-6 max-sm:grid-cols-1 max-sm:gap-3 max-sm:px-4"
                    )}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div
                      className={arClass(
                        `text-center p-3 rounded-xl border max-sm:p-2 min-w-max ${
                          darkMode
                            ? "bg-blue-800/20 border-blue-700/50"
                            : "bg-blue-50 border-blue-200"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                            darkMode ? "text-blue-300" : "text-blue-600"
                          }`
                        )}
                      >
                        {textTranslator("Age", "PATIENTS")}
                      </p>
                      <p
                        className={arClass(
                          `text-2xl font-bold max-sm:text-sm ${colors.text.primary}`
                        )}
                      >
                        {calculateAge
                          ? calculateAge(selectedPatient.birthday)
                          : selectedPatient.age}
                      </p>
                    </div>

                    <div
                      className={arClass(
                        `text-center p-3 rounded-xl border max-sm:p-2 min-w-max ${
                          darkMode
                            ? "bg-purple-800/20 border-purple-700/50"
                            : "bg-purple-50 border-purple-200"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                            darkMode ? "text-purple-300" : "text-purple-600"
                          }`
                        )}
                      >
                        {textTranslator("Gender", "PATIENTS")}
                      </p>
                      <p
                        className={arClass(
                          `text-lg font-bold max-sm:text-sm ${colors.text.primary}`
                        )}
                      >
                        <Text>{selectedPatient.gender}</Text>
                      </p>
                    </div>

                    <div
                      className={arClass(
                        `text-center p-3 rounded-xl border max-sm:p-2 min-w-max ${
                          darkMode
                            ? "bg-red-800/20 border-red-700/50"
                            : "bg-red-50 border-red-200"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                            darkMode ? "text-red-300" : "text-red-600"
                          }`
                        )}
                      >
                        {textTranslator("Blood Type", "PATIENTS")}
                      </p>
                      <p
                        className={arClass(
                          `text-lg font-bold max-sm:text-sm ${colors.text.primary}`
                        )}
                      >
                        {selectedPatient.bloodType || "—"}
                      </p>
                    </div>

                    <div
                      className={arClass(
                        `text-center p-3 rounded-xl border max-sm:p-2 min-w-max ${
                          darkMode
                            ? "bg-green-800/20 border-green-700/50"
                            : "bg-green-50 border-green-200"
                        }`
                      )}
                    >
                      <p
                        className={arClass(
                          `text-xs font-semibold uppercase tracking-wide mb-2 max-sm:text-xsm max-sm:mb-1 ${
                            darkMode ? "text-green-300" : "text-green-600"
                          }`
                        )}
                      >
                        {textTranslator("Civil Status", "PATIENTS")}
                      </p>
                      <p
                        className={arClass(
                          `text-sm font-bold max-sm:text-xs ${colors.text.primary} leading-tight`
                        )}
                      >
                        <Text>{selectedPatient.civilStatus || "—"}</Text>
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Content Sections - Scrollable */}
                <div
                  className={arClass(
                    "p-6 space-y-6 max-sm:p-4 max-sm:space-y-4 overflow-y-auto"
                  )}
                >
                  {/* Personal Information */}
                  <motion.section
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.6 }}
                  >
                    <h4
                      className={arClass(
                        `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                      )}
                    >
                      <FaUser className="text-blue-500 max-sm:w-4 max-sm:h-4" />
                      <Text>Personal Information</Text>
                    </h4>
                    <div
                      className={arClass(
                        "grid grid-cols-2 gap-4 max-sm:grid-cols-1 max-sm:gap-2"
                      )}
                    >
                      <div>
                        <span
                          className={arClass(
                            `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`
                          )}
                        >
                          <Text>Birthday</Text>
                        </span>
                        <p
                          className={arClass(
                            `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                          )}
                        >
                          {formatDate(selectedPatient.birthday, false) || "—"}
                        </p>
                      </div>
                      <div>
                        <span
                          className={arClass(
                            `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`
                          )}
                        >
                          <Text>CIN</Text>
                        </span>
                        <p
                          className={arClass(
                            `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                          )}
                        >
                          {selectedPatient.cin || "—"}
                        </p>
                      </div>
                      <div className="col-span-2 max-sm:col-span-1">
                        <span
                          className={arClass(
                            `text-xs font-medium max-sm:text-xsm ${colors.text.muted} block mb-1`
                          )}
                        >
                          <Text>Address</Text>
                        </span>
                        <p
                          className={arClass(
                            `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                          )}
                        >
                          {selectedPatient.address || "—"}
                        </p>
                      </div>
                    </div>
                  </motion.section>

                  {/* Contact Information */}
                  <motion.section
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.7 }}
                  >
                    <h4
                      className={arClass(
                        `text-lg font-semibold mb-4 max-sm:text-sm max-sm:mb-3 ${colors.text.primary} flex items-center gap-2`
                      )}
                    >
                      <FaUser className="text-green-500 max-sm:w-4 max-sm:h-4" />
                      <Text>Contact Information</Text>
                    </h4>
                    <div className={arClass("space-y-3 max-sm:space-y-2")}>
                      <div
                        className={arClass(
                          "flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-green-500/5 to-emerald-500/5 max-sm:p-2"
                        )}
                      >
                        <span
                          className={arClass(
                            `text-sm max-sm:text-xs ${colors.text.muted}`
                          )}
                        >
                          <Text>Phone</Text>
                        </span>
                        <span
                          className={arClass(
                            `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                          )}
                        >
                          {selectedPatient.phone || "—"}
                        </span>
                      </div>
                      <div
                        className={arClass(
                          "flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-blue-500/5 to-cyan-500/5 max-sm:p-2"
                        )}
                      >
                        <span
                          className={arClass(
                            `text-sm max-sm:text-xs ${colors.text.muted}`
                          )}
                        >
                          <Text>Email</Text>
                        </span>
                        <span
                          className={arClass(
                            `text-sm font-medium max-sm:text-xs ${colors.text.primary}`
                          )}
                        >
                          {selectedPatient.email || "—"}
                        </span>
                      </div>
                    </div>
                  </motion.section>
                  <motion.a
                    href={`/patients/${selectedPatient.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={arClass(
                      `w-full mt-3 py-2 px-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors max-sm:text-xs ${
                        darkMode
                          ? "bg-red-500/20 hover:bg-red-500/30 text-red-300"
                          : "bg-red-100 hover:bg-red-200 text-red-700"
                      }`
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FaEye />
                    <Text>View Full Patient</Text>
                  </motion.a>
                </div>
              </motion.div>
            ) : (
              // Empty Data
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={arClass(
                  "p-6 flex-1 flex flex-col justify-center items-center max-sm:p-4"
                )}
              >
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse",
                  }}
                  className="mb-6 max-sm:mb-4"
                >
                  <FaUser
                    className={arClass(
                      `w-20 h-20 max-sm:w-16 max-sm:h-16 ${colors.text.muted}`
                    )}
                  />
                </motion.div>
                <motion.h4
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className={arClass(
                    `text-xl font-semibold mb-3 max-sm:text-lg max-sm:mb-2 ${colors.text.primary}`
                  )}
                >
                  <Text>No Patient Selected</Text>
                </motion.h4>
                <motion.p
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className={arClass(
                    `text-sm text-center max-w-xs max-sm:text-xs ${colors.text.secondary}`
                  )}
                >
                  <Text>
                    Select a patient from the list to view their complete
                    details and medical information here.
                  </Text>
                </motion.p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PatientSelection;